/**
 * Default Live model (Gemini API preview). The server token callable defaults to the
 * same value (functions/src/voice/geminiLiveToken.js). Override via env to A/B a faster
 * live model without a code change; keep REACT_APP_GEMINI_LIVE_MODEL and the function's
 * GEMINI_LIVE_MODEL_DEFAULT in sync, or set a per-variant `translateModel` in the fork
 * config (flows through mintDirectionToken -> token payload).
 */
export const GEMINI_LIVE_MODEL_DEFAULT =
  process.env.REACT_APP_GEMINI_LIVE_MODEL || 'gemini-3.1-flash-live-preview';

/** Current native conversational Live model (CareStart dashboard `gemini_live` default). */
export const GEMINI_LIVE_MODEL_31_FLASH_PREVIEW = 'gemini-3.1-flash-live-preview';

/** Direct upgrade path from Flash Live. Selectable on the CareStart dashboard engine only. */
export const GEMINI_LIVE_MODEL_38 = 'gemini-3.8-live';

/** Conversational Live models the CareStart dashboard engine may request. */
export const CARESTART_GEMINI_LIVE_MODELS = [
  GEMINI_LIVE_MODEL_31_FLASH_PREVIEW,
  GEMINI_LIVE_MODEL_38,
];

export const CARESTART_GEMINI_LIVE_MODEL_DEFAULT = GEMINI_LIVE_MODEL_31_FLASH_PREVIEW;

export const CARESTART_GEMINI_LIVE_MODEL_STORAGE_KEY = 'hd_carestart_gemini_live_model';

/**
 * @param {unknown} modelId
 * @returns {string}
 */
export function normalizeGeminiLiveModelId(modelId) {
  if (typeof modelId !== 'string') return '';
  return modelId.trim().replace(/^models\//, '');
}

const CARESTART_GEMINI_LIVE_MODEL_FROM_ENV = normalizeGeminiLiveModelId(
  process.env.REACT_APP_CARESTART_GEMINI_LIVE_MODEL
);

/**
 * CareStart dashboard default (build-time). Does not change {@link GEMINI_LIVE_MODEL_DEFAULT},
 * which VoxPont / Translate / other callers still inherit.
 */
export const CARESTART_GEMINI_LIVE_MODEL = CARESTART_GEMINI_LIVE_MODELS.includes(
  CARESTART_GEMINI_LIVE_MODEL_FROM_ENV
)
  ? CARESTART_GEMINI_LIVE_MODEL_FROM_ENV
  : CARESTART_GEMINI_LIVE_MODEL_DEFAULT;

/**
 * Exact Gemini 3.8 Live conversational model (not Translate, not Extended Thinking).
 * @param {unknown} modelId
 * @returns {boolean}
 */
export function isGemini38LiveModel(modelId) {
  return normalizeGeminiLiveModelId(modelId) === GEMINI_LIVE_MODEL_38;
}

/**
 * @param {unknown} modelId
 * @returns {boolean}
 */
export function isCareStartGeminiLiveModel(modelId) {
  return CARESTART_GEMINI_LIVE_MODELS.includes(normalizeGeminiLiveModelId(modelId));
}

/**
 * Resolve the CareStart dashboard Live model: explicit candidate, then localStorage,
 * then {@link CARESTART_GEMINI_LIVE_MODEL}, then 3.1 Flash Live.
 *
 * @param {unknown} [candidate]
 * @returns {string}
 */
export function resolveCareStartGeminiLiveModel(candidate) {
  const fromCandidate = normalizeGeminiLiveModelId(candidate);
  if (CARESTART_GEMINI_LIVE_MODELS.includes(fromCandidate)) return fromCandidate;

  if (typeof window !== 'undefined') {
    try {
      const stored = normalizeGeminiLiveModelId(
        window.localStorage.getItem(CARESTART_GEMINI_LIVE_MODEL_STORAGE_KEY)
      );
      if (CARESTART_GEMINI_LIVE_MODELS.includes(stored)) return stored;
    } catch {
      /* private mode / SSR */
    }
  }

  return CARESTART_GEMINI_LIVE_MODEL;
}

/**
 * Persist a CareStart dashboard Live-model choice. Unknown values fall back to the default.
 *
 * @param {unknown} modelId
 * @returns {string} The allowlisted model that was stored (or the default).
 */
export function persistCareStartGeminiLiveModel(modelId) {
  const resolved = isCareStartGeminiLiveModel(modelId)
    ? normalizeGeminiLiveModelId(modelId)
    : CARESTART_GEMINI_LIVE_MODEL;
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(CARESTART_GEMINI_LIVE_MODEL_STORAGE_KEY, resolved);
    } catch {
      /* ignore quota / private mode */
    }
  }
  return resolved;
}

/**
 * When the native Live engine is given an explicit `liveModel` prop (dashboard only),
 * coerce it to an allowlisted id. Empty/missing returns undefined so the shared
 * {@link GEMINI_LIVE_MODEL_DEFAULT} token path stays unchanged for other callers.
 *
 * @param {unknown} liveModel
 * @returns {string|undefined}
 */
export function coerceCareStartGeminiLiveModel(liveModel) {
  if (typeof liveModel !== 'string' || !liveModel.trim()) return undefined;
  const normalized = normalizeGeminiLiveModelId(liveModel);
  return CARESTART_GEMINI_LIVE_MODELS.includes(normalized)
    ? normalized
    : CARESTART_GEMINI_LIVE_MODEL_DEFAULT;
}

/** Callable name in Firebase Functions (see functions/src/index.js). */
export const GEMINI_LIVE_TOKEN_CALLABLE = 'getGeminiLiveEphemeralToken';

/** Must match deployed region (URL is us-central1-PROJECT.cloudfunctions.net/...). */
export const GEMINI_LIVE_FUNCTIONS_REGION =
  process.env.REACT_APP_FIREBASE_FUNCTIONS_REGION || 'us-central1';

/**
 * Regions the token function is deployed to (multi-region). Comma-separated env list,
 * defaulting to the single primary region so behavior is unchanged until ops both
 * deploys extra regions (GEMINI_LIVE_TOKEN_REGIONS on the function) AND lists them
 * here. Every entry MUST be a region the function is actually deployed to, or the
 * callable URL 404s for users routed there.
 */
export const GEMINI_LIVE_FUNCTIONS_REGIONS = (
  process.env.REACT_APP_FIREBASE_FUNCTIONS_REGIONS || GEMINI_LIVE_FUNCTIONS_REGION
)
  .split(',')
  .map((r) => r.trim())
  .filter(Boolean);

/**
 * Pick the nearest deployed region for the current client to minimize the token-mint
 * round trip at session start. Uses the browser timezone continent as a cheap proxy
 * for geography (no network/geo-IP call). Falls back to the primary region whenever a
 * continent has no matching deployed region, so it can only ever return a region from
 * the configured (deployed) list.
 *
 * @returns {string}
 */
export function resolveGeminiLiveFunctionsRegion() {
  const regions = GEMINI_LIVE_FUNCTIONS_REGIONS;
  if (regions.length <= 1) return regions[0] || GEMINI_LIVE_FUNCTIONS_REGION;

  let continent = '';
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    continent = tz.split('/')[0] || '';
  } catch {
    /* Intl unavailable — fall through to primary */
  }

  const prefixForContinent = {
    Europe: 'europe-',
    Africa: 'europe-',
    Asia: 'asia-',
    Australia: 'australia-',
    Pacific: 'australia-',
    America: 'us-',
  };
  const wanted = prefixForContinent[continent];
  if (wanted) {
    const match = regions.find((r) => r.startsWith(wanted));
    if (match) return match;
  }
  return regions[0];
}

/** Tool name used to bridge scripted flows to {@code onPauseDetected}. */
export const GEMINI_LIVE_SCRIPT_TOOL_NAME = 'get_next_utterance';
